import { useTranslation } from '@dreamstack/i18n'
import { ChevronLeft, ChevronRight } from '@dreamstack/icons'
import { Disclosure } from '@headlessui/react'
import CloseIcon from '@material-ui/icons/Close'
import type { FunctionComponent } from 'react'
import tw, { styled } from 'twin.macro'
import { BrokerAvatar } from '..'
import { Text_13_18 } from '../index'

const StyledChevronLeft = styled(ChevronLeft)<{ $small?: boolean }>(
  ({ $small }) => [
    tw`text-accentroWhite-full cursor-pointer transform -rotate-90 md:rotate-0`,
    $small
      ? tw`md:(h-px-24 w-px-24 mt-0) lg:(h-px-40 w-px-40 h-px-40)`
      : tw`h-px-40 w-px-40 md:(mt-px-8) lg:(h-px-40) `,
  ]
)
const StyledChevronRight = styled(ChevronRight)<{ $small?: boolean }>(
  ({ $small }) => [
    tw`text-accentroWhite-full cursor-pointer transform -rotate-90 md:rotate-0`,
    $small
      ? tw`md:(h-px-24 w-px-24 mt-0) lg:(h-px-40 w-px-40 h-px-40)`
      : tw`h-px-40 w-px-40 md:(mt-px-8) lg:(h-px-40) `,
  ]
)
//more padding is needed only on small screens on project page
const StyledDisclosureButton = styled(Disclosure.Button)<{
  $small?: boolean
  $morePadding?: boolean
  children: JSX.Element[]
}>(({ $small, $morePadding }) => [
  tw`py-px-16 w-full flex items-center justify-between focus:outline-none md:(flex-col px-0)`,
  $small
    ? tw`px-px-16 md:(w-px-40) lg:(w-px-80 px-px-16)`
    : tw`px-px-16 md:w-px-80`,
  $morePadding && tw`px-px-24 md:(px-px-16)`,
])

const BrokerAvatarBlockWrapper = styled.div<{ open: boolean }>`
  ${({ open }) => open && tw`md:hidden`}
`
const StyledDisclosure = styled(Disclosure)<{
  isProject?: boolean
  children?: ({ open }: { open: boolean }) => React.ReactNode
  $small?: boolean
}>(({ isProject }) => [
  isProject ? tw`-mt-px-40 md:(-mt-px-64) ` : tw`mt-px-40 md:(mt-px-64) `,
  tw`z-10 col-span-full md:(absolute h-px-320) bg-accentroGray-full right-0 rounded-none focus:outline-none flex`,
])
const ChevronWrapper = styled.div<{ $small?: boolean }>(({ $small }) => [
  $small && tw`md:pt-px-20 lg:(py-px-8)`,
])

const ContactPersonText = styled.span<{ $small?: boolean }>(({ $small }) => [
  tw`text-accentroWhite-full transform font-semibold text-base tracking-wide md:(vertical-lr rotate-180)`,
  $small ? tw`md:pb-px-32 lg:(pb-px-24)` : tw`md:pb-px-24`,
])

const StyledPanel = styled(Disclosure.Panel)<{
  children: JSX.Element[]
  $small?: boolean
}>(({ $small }) => [
  tw`w-full h-auto px-px-24 pb-px-16 md:(w-60 h-full p-px-20)`,
  $small && tw`md:(border-t-2 border-l-2 border-b-2 border-accentroWhite-200)`,
])

export const PropertyContact: FunctionComponent<
  React.PropsWithChildren<{
    small?: boolean
    morePadding?: boolean
    broker_email?: string | null
    broker_avatar_url?: string | null
    broker_first_name?: string | null
    broker_last_name?: string | null
    broker_phone?: string | null
  }>
> = ({
  broker_email,
  broker_avatar_url,
  broker_first_name,
  broker_last_name,
  broker_phone,
  small,
  morePadding,
}) => {
  const brokerEmail = broker_email ?? 'kundenmanagement@accentro.de'
  const brokerEmailWithSoftBreak = brokerEmail.split('@').join('&#8203;@')
  const name = broker_last_name
    ? [broker_first_name, broker_last_name].join(' ')
    : 'Accentro Info'
  const phone = broker_phone ?? '030 - 88 71 81 41'
  const t = useTranslation()
  return (
    <StyledDisclosure $small={small}>
      {({ open }: { open: boolean }) => (
        <div tw="flex flex-col md:flex-row">
          <BrokerAvatarBlockWrapper open={open}>
            <StyledDisclosureButton $small={small} $morePadding={morePadding}>
              <BrokerAvatar extraSmall={small} url={broker_avatar_url} />
              <ContactPersonText $small={small}>
                {t('accentro:contactPerson')}
              </ContactPersonText>
              <ChevronWrapper $small={small}>
                {open ? (
                  <StyledChevronRight $small={small} />
                ) : (
                  <StyledChevronLeft $small={small} />
                )}
              </ChevronWrapper>
            </StyledDisclosureButton>
          </BrokerAvatarBlockWrapper>
          <StyledPanel $small={small}>
            <div tw="hidden md:(flex justify-between items-center)">
              <Text_13_18 tw="font-semibold text-accentroWhite-full">
                {t('accentro:contactPerson')}
              </Text_13_18>
              <Disclosure.Button tw="text-accentroWhite-full focus:outline-none">
                <CloseIcon />
              </Disclosure.Button>
            </div>
            <div tw="h-full flex flex-col justify-center space-y-px-16 md:(items-start space-y-px-24)">
              <div tw="hidden md:block">
                <BrokerAvatar big url={broker_avatar_url} />
              </div>
              <div tw="text-accentroWhite-full font-semibold text-px-20 md:text-px-16">
                {name}
              </div>
              <div tw="flex flex-col space-y-px-8">
                <div>
                  <Text_13_18 tw="font-semibold text-accentroWhite-full">
                    {t('accentro:contact.phone')}
                  </Text_13_18>
                  <a href={`tel:${phone}`}>
                    <Text_13_18 tw="text-accentroWhite-full">
                      {phone}
                    </Text_13_18>
                  </a>
                </div>
                <div>
                  <Text_13_18 tw="font-semibold text-accentroWhite-full">
                    {t('accentro:contact.email')}
                  </Text_13_18>
                  <a href={`mailto:${brokerEmail}`}>
                    <Text_13_18 tw="text-accentroAquaMidLight-full font-semibold break-words">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: brokerEmailWithSoftBreak,
                        }}
                      />
                    </Text_13_18>
                  </a>
                </div>
              </div>
            </div>
          </StyledPanel>
        </div>
      )}
    </StyledDisclosure>
  )
}
